import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@copro/api';
import { enqueueSnackbar } from '../../../components/GlobalSnackbar/snackbarStore';
import { keys } from './keys';

type UpdateAISettingsParams = {
  spaceId: string;
  isAiEnabled: boolean;
};

async function updateSpaceAISettings({ spaceId, isAiEnabled }: UpdateAISettingsParams) {
  const { data, error } = await apiClient
    .from('spaces')
    .update({ is_ai_enabled: isAiEnabled })
    .eq('id', spaceId)
    .select('id, is_ai_enabled')
    .single();

  if (error) {
    throw error;
  }

  return data;
}

export function useUpdateSpaceAISettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateSpaceAISettings,
    onMutate: async ({ spaceId, isAiEnabled }) => {
      await queryClient.cancelQueries({ queryKey: keys.aiSettings(spaceId) });
      const previousValue = queryClient.getQueryData<boolean>(keys.aiSettings(spaceId));
      queryClient.setQueryData(keys.aiSettings(spaceId), isAiEnabled);
      return { previousValue, spaceId };
    },
    onError: (err, _vars, context) => {
      if (context) {
        queryClient.setQueryData(keys.aiSettings(context.spaceId), context.previousValue);
      }
      enqueueSnackbar(
        `Erreur mise à jour IA : ${err instanceof Error ? err.message : 'Action impossible'}`,
        'error'
      );
    },
    onSuccess: (data) => {
      if (data?.id) {
        queryClient.setQueryData(keys.aiSettings(data.id), data.is_ai_enabled);
        enqueueSnackbar(
          data.is_ai_enabled ? 'IA activée pour la copropriété.' : 'IA désactivée pour la copropriété.',
          'success'
        );
      }
    },
  });
}
